/**
 * React Query hooks for the cross-tenant super-admin panel.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { AdminService } from '../services/admin';

function useAdminService() {
  const { getToken } = useAuth();
  return useMemo(() => new AdminService(getToken), [getToken]);
}

export function useAdminAccess() {
  const service = useAdminService();
  return useQuery({
    queryKey: ['admin', 'access'],
    queryFn: ({ signal }) => service.getAccess({ signal }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useAdminOverview(options = {}) {
  const service = useAdminService();
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: ({ signal }) => service.getOverview({ signal }),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useAdminTenants(options = {}) {
  const service = useAdminService();
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: ({ signal }) => service.getTenants({ signal }),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useAdminAiUsage(options = {}) {
  const service = useAdminService();
  return useQuery({
    queryKey: ['admin', 'ai-usage'],
    queryFn: ({ signal }) => service.getAiUsage({ signal }),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useSetAgentConfig() {
  const service = useAdminService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => service.setAgentConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}
